import { useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fireHeartsConfetti } from "@/lib/confetti";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DollarSign, Calendar, Clock, MapPin, Loader2, X, Check } from "lucide-react";

export default function CounterOfferResponseModal({ isOpen, onClose, offer, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null); // 'accept', 'decline', 'counter'
  const [newCounterAmount, setNewCounterAmount] = useState("");
  const [counterMessage, setCounterMessage] = useState("");

  const handleRespond = async (selectedAction) => {
    setLoading(true);

    try {
      const payload = {
        action: selectedAction
      };

      if (selectedAction === "counter") {
        const amount = parseFloat(newCounterAmount);
        if (amount < 100 || amount > 1000) {
          toast.error("Counter amount must be between $100 and $1000");
          setLoading(false);
          return;
        }
        payload.counter_amount = amount;
        payload.counter_message = counterMessage;
      }

      await api.post(`/date-offers/${offer.offer_id}/counter-respond`, payload);

      if (selectedAction === "accept") {
        toast.success("Counter-offer accepted! Complete payment to confirm the date.");
        fireHeartsConfetti();
      } else if (selectedAction === "decline") {
        toast.success("Counter-offer declined. Your slot is now available.");
      } else if (selectedAction === "counter") {
        toast.success("Counter-offer sent back!");
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond");
    } finally {
      setLoading(false);
    }
  };

  if (!offer) return null;

  const woman = offer.woman || {};
  // Original amount is always stored in offer.offer_amount
  const originalAmount = offer.offer_amount;
  const counterAmount = offer.counter_amount;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Counter-Offer from {woman.name}</DialogTitle>
          <DialogDescription>
            They proposed a different amount for your date offer
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Woman's Profile */}
          <div className="bg-primary/10 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <img
                src={woman.photos?.[0] || woman.picture || "/placeholder.svg"}
                alt={woman.name}
                className="h-16 w-16 rounded-full object-cover"
              />
              <div>
                <p className="font-semibold text-lg">{woman.name}</p>
                <p className="text-sm text-muted-foreground">{woman.age} years old</p>
              </div>
            </div>
          </div>

          {/* Offer Comparison */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted rounded-lg p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Your Offer</p>
              <p className="text-2xl font-bold text-muted-foreground line-through">
                ${originalAmount}
              </p>
            </div>

            <div className="bg-orange-500/10 rounded-lg p-4 text-center border-2 border-orange-500">
              <p className="text-xs text-muted-foreground mb-1">Their Counter</p>
              <p className="text-2xl font-bold text-orange-500">
                ${counterAmount}
              </p>
            </div>
          </div>

          {/* Date Details */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Date:</span>
              <span>{offer.date_details?.proposed_date}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Time:</span>
              <span>{offer.date_details?.proposed_time}</span>
            </div>

            {offer.date_details?.duration_hours ? (
              <div className="flex items-center gap-2 text-sm" data-testid="counter-offer-duration">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Duration:</span>
                <span>{offer.date_details.duration_hours} hour{offer.date_details.duration_hours === 1 ? "" : "s"}</span>
              </div>
            ) : null}

            {offer.date_details?.date_type && (
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Type:</span>
                <span className="capitalize">{offer.date_details.date_type.replace('_', ' ')}</span>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Location:</span>
              <span>{offer.date_details?.location}</span>
            </div>
          </div>

          {offer.counter_message && (
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm font-medium mb-1">Their Message:</p>
              <p className="text-sm text-muted-foreground">
                &quot;{offer.counter_message}&quot;
              </p>
            </div>
          )}

          {/* Action Selection */}
          {!action && (
            <div className="space-y-2 pt-4">
              <Button 
                className="w-full" 
                size="lg"
                onClick={() => setAction("accept")}
              >
                <Check className="mr-2 h-4 w-4" />
                Accept ${counterAmount}
              </Button>

              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => setAction("counter")}
              >
                <DollarSign className="mr-2 h-4 w-4" />
                Counter Back
              </Button>

              <Button 
                className="w-full" 
                variant="destructive"
                onClick={() => setAction("decline")}
              >
                <X className="mr-2 h-4 w-4" />
                Decline
              </Button>
            </div>
          )}

          {/* Accept Confirmation */}
          {action === "accept" && (
            <div className="space-y-4 pt-4 border-t">
              <div className="bg-green-500/10 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Confirm Acceptance</p>
                <p className="text-xs text-muted-foreground">
                  You&apos;ll proceed to payment for ${counterAmount}. 
                  You pay: ${counterAmount} (Platform keeps 20%, they receive 80%).
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setAction(null)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => handleRespond("accept")}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Accept & Pay
                </Button>
              </div>
            </div>
          )}

          {/* Decline Confirmation */}
          {action === "decline" && (
            <div className="space-y-4 pt-4 border-t">
              <div className="bg-red-500/10 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Decline Counter-Offer</p>
                <p className="text-xs text-muted-foreground">
                  This will decline the counter-offer and free up your slot.
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setAction(null)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => handleRespond("decline")}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirm Decline
                </Button>
              </div>
            </div>
          )}

          {/* Counter Back Form */}
          {action === "counter" && (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label htmlFor="counter_amount_back">
                  <DollarSign className="inline h-4 w-4" /> Your Counter Amount *
                </Label>
                <Input
                  id="counter_amount_back"
                  type="number"
                  min="100"
                  max="1000"
                  step="10"
                  placeholder="Enter amount (100-1000)"
                  value={newCounterAmount}
                  onChange={(e) => setNewCounterAmount(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Original: ${originalAmount}, Their counter: ${offer.counter_amount}
                </p>
              </div>

              <div>
                <Label htmlFor="counter_message_back">Message (Optional)</Label>
                <Textarea
                  id="counter_message_back"
                  placeholder="Explain your counter-offer..."
                  value={counterMessage}
                  onChange={(e) => setCounterMessage(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setAction(null)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => handleRespond("counter")}
                  disabled={loading || !newCounterAmount}
                  className="flex-1"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Counter
                </Button>
              </div>
            </div>
          )}
        </div>

        {!action && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} className="w-full">
              Close
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
